import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';
import './SetupHousekeepingScreen.css';

function SetupHousekeepingScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('clean-status');

  // Clean Status state
  const [cleanStatuses, setCleanStatuses] = useState([]);
  const [loadingCleanStatuses, setLoadingCleanStatuses] = useState(true);
  const [savingCleanStatus, setSavingCleanStatus] = useState(false);
  
  // Clean Status Modal state
  const [showCleanStatusModal, setShowCleanStatusModal] = useState(false);
  const [cleanStatusModalMode, setCleanStatusModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedCleanStatus, setSelectedCleanStatus] = useState(null);
  const [cleanStatusForm, setCleanStatusForm] = useState({ code: '', description: '', active: true, sort_order: 0 });
  const [cleanStatusErrors, setCleanStatusErrors] = useState({});

  // Clean Zones state
  const [cleaningZones, setCleaningZones] = useState([]);
  const [loadingCleaningZones, setLoadingCleaningZones] = useState(true);
  const [savingCleaningZone, setSavingCleaningZone] = useState(false);
  
  // Clean Zones Modal state
  const [showCleaningZoneModal, setShowCleaningZoneModal] = useState(false);
  const [cleaningZoneModalMode, setCleaningZoneModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedCleaningZone, setSelectedCleaningZone] = useState(null);
  const [cleaningZoneForm, setCleaningZoneForm] = useState({ code: '', description: '', active: true });
  const [cleaningZoneErrors, setCleaningZoneErrors] = useState({});

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  // Fetch data on component mount
  useEffect(() => {
    fetchCleanStatuses();
    fetchCleaningZones();
  }, []);

  // API Functions for Clean Statuses
  const fetchCleanStatuses = async () => {
    try {
      setLoadingCleanStatuses(true);
      const response = await fetch(`${API_URL}/clean-statuses`);
      if (!response.ok) throw new Error('Failed to fetch clean statuses');
      const data = await response.json();
      
      // Sort by sort_order, then by code for consistent ordering
      const sortedData = data.sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return (a.sort_order || 0) - (b.sort_order || 0);
        }
        return a.code.localeCompare(b.code);
      });
      
      setCleanStatuses(sortedData);
    } catch (error) {
      console.error('Error fetching clean statuses:', error);
      setMessage({ text: 'Failed to load clean statuses', variant: 'danger', show: true });
    } finally {
      setLoadingCleanStatuses(false);
    }
  };

  const updateCleanStatus = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/clean-statuses/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update clean status');
      }
      
      const updatedStatus = await response.json();
      setCleanStatuses(cleanStatuses.map(cs => cs.id === id ? updatedStatus : cs));
      setMessage({ text: 'Clean status updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating clean status:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteCleanStatus = async (id) => {
    if (!window.confirm('Are you sure you want to delete this clean status?')) return;
    
    try {
      const response = await fetch(`${API_URL}/clean-statuses/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete clean status');
      }
      
      setCleanStatuses(cleanStatuses.filter(cs => cs.id !== id));
      setMessage({ text: 'Clean status deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting clean status:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const moveCleanStatus = async (id, direction) => {
    const currentIndex = cleanStatuses.findIndex(cs => cs.id === id);
    if (currentIndex === -1) return;
    
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex === -1 || targetIndex >= cleanStatuses.length) return;
    
    const currentStatus = cleanStatuses[currentIndex];
    const targetStatus = cleanStatuses[targetIndex];
    
    try {
      setMessage({ text: 'Updating sort order...', variant: 'info', show: true });
      
      // Swap sort orders
      const newCurrentSortOrder = targetStatus.sort_order;
      const newTargetSortOrder = currentStatus.sort_order;
      
      // Update both statuses
      await Promise.all([
        fetch(`${API_URL}/clean-statuses/${currentStatus.id}/sort-order`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sort_order: newCurrentSortOrder })
        }),
        fetch(`${API_URL}/clean-statuses/${targetStatus.id}/sort-order`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sort_order: newTargetSortOrder })
        })
      ]);
      
      // Refresh the list
      fetchCleanStatuses();
      setMessage({ text: 'Sort order updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating sort order:', error);
      setMessage({ text: 'Failed to update sort order', variant: 'danger', show: true });
    }
  };

  // API Functions for Cleaning Zones
  const fetchCleaningZones = async () => {
    try {
      setLoadingCleaningZones(true);
      const response = await fetch(`${API_URL}/cleaning-zones`);
      if (!response.ok) throw new Error('Failed to fetch cleaning zones');
      const data = await response.json();
      setCleaningZones(data);
    } catch (error) {
      console.error('Error fetching cleaning zones:', error);
      setMessage({ text: 'Failed to load cleaning zones', variant: 'danger', show: true });
    } finally {
      setLoadingCleaningZones(false);
    }
  };

  const updateCleaningZone = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/cleaning-zones/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update cleaning zone');
      }
      
      const updatedZone = await response.json();
      setCleaningZones(cleaningZones.map(cz => cz.id === id ? updatedZone : cz));
      setMessage({ text: 'Cleaning zone updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating cleaning zone:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteCleaningZone = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cleaning zone?')) return;
    
    try {
      const response = await fetch(`${API_URL}/cleaning-zones/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete cleaning zone');
      }
      
      setCleaningZones(cleaningZones.filter(cz => cz.id !== id));
      setMessage({ text: 'Cleaning zone deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting cleaning zone:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Clean Status Modal handlers
  const handleAddCleanStatus = () => {
    setCleanStatusModalMode('add');
    setSelectedCleanStatus(null);
    setCleanStatusForm({ code: '', description: '', active: true, sort_order: 0 });
    setCleanStatusErrors({});
    setShowCleanStatusModal(true);
  };

  const handleEditCleanStatus = (cleanStatus) => {
    setCleanStatusModalMode('edit');
    setSelectedCleanStatus(cleanStatus);
    setCleanStatusForm({ 
      code: cleanStatus.code, 
      description: cleanStatus.description, 
      active: cleanStatus.active,
      sort_order: cleanStatus.sort_order || 0
    });
    setCleanStatusErrors({});
    setShowCleanStatusModal(true);
  };

  const handleCleanStatusInputChange = (field, value) => {
    setCleanStatusForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (cleanStatusErrors[field]) {
      setCleanStatusErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateCleanStatusForm = () => {
    const errors = {};
    if (!cleanStatusForm.code.trim()) {
      errors.code = 'Code is required';
    }
    if (cleanStatusForm.sort_order === undefined || cleanStatusForm.sort_order === null || cleanStatusForm.sort_order < 0) {
      errors.sort_order = 'Sort order must be a non-negative number';
    }
    return errors;
  };

  const handleSaveCleanStatus = async () => {
    const errors = validateCleanStatusForm();
    if (Object.keys(errors).length > 0) {
      setCleanStatusErrors(errors);
      return;
    }

    try {
      setSavingCleanStatus(true);
      if (cleanStatusModalMode === 'add') {
        const response = await fetch(`${API_URL}/clean-statuses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cleanStatusForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create clean status');
        }
        
        const newStatus = await response.json();
        setCleanStatuses([...cleanStatuses, newStatus]);
        setMessage({ text: 'Clean status created successfully', variant: 'success', show: true });
      } else {
        await updateCleanStatus(selectedCleanStatus.id, cleanStatusForm);
      }
      
      setShowCleanStatusModal(false);
      setCleanStatusForm({ code: '', description: '', active: true });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingCleanStatus(false);
    }
  };

  const handleCloseCleanStatusModal = () => {
    setShowCleanStatusModal(false);
    setCleanStatusForm({ code: '', description: '', active: true, sort_order: 0 });
    setCleanStatusErrors({});
  };

  // Clean Zones Modal handlers
  const handleAddCleaningZone = () => {
    setCleaningZoneModalMode('add');
    setSelectedCleaningZone(null);
    setCleaningZoneForm({ code: '', description: '', active: true });
    setCleaningZoneErrors({});
    setShowCleaningZoneModal(true);
  };

  const handleEditCleaningZone = (cleaningZone) => {
    setCleaningZoneModalMode('edit');
    setSelectedCleaningZone(cleaningZone);
    setCleaningZoneForm({ code: cleaningZone.code, description: cleaningZone.description, active: cleaningZone.active });
    setCleaningZoneErrors({});
    setShowCleaningZoneModal(true);
  };

  const handleCleaningZoneInputChange = (field, value) => {
    setCleaningZoneForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (cleaningZoneErrors[field]) {
      setCleaningZoneErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateCleaningZoneForm = () => {
    const errors = {};
    if (!cleaningZoneForm.code.trim()) {
      errors.code = 'Code is required';
    }
    return errors;
  };

  const handleSaveCleaningZone = async () => {
    const errors = validateCleaningZoneForm();
    if (Object.keys(errors).length > 0) {
      setCleaningZoneErrors(errors);
      return;
    }

    try {
      setSavingCleaningZone(true);
      if (cleaningZoneModalMode === 'add') {
        const response = await fetch(`${API_URL}/cleaning-zones`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cleaningZoneForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create cleaning zone');
        }
        
        const newZone = await response.json();
        setCleaningZones([...cleaningZones, newZone]);
        setMessage({ text: 'Cleaning zone created successfully', variant: 'success', show: true });
      } else {
        await updateCleaningZone(selectedCleaningZone.id, cleaningZoneForm);
      }
      
      setShowCleaningZoneModal(false);
      setCleaningZoneForm({ code: '', description: '', active: true });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingCleaningZone(false);
    }
  };

  const handleCloseCleaningZoneModal = () => {
    setShowCleaningZoneModal(false);
    setCleaningZoneForm({ code: '', description: '', active: true });
    setCleaningZoneErrors({});
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('clean-status');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('clean-status');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message.show) {
      const timer = setTimeout(() => {
        setMessage({ text: '', variant: '', show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.show]);

  return (
    <Container fluid className="setup-housekeeping-screen">
      <div className="screen-header">
        <h1>Setup - Housekeeping</h1>
        <p>Configure housekeeping settings and management</p>
      </div>

      <div className="screen-content">
        {/* Message Alert */}
        {message.show && (
          <Alert 
            variant={message.variant} 
            dismissible 
            onClose={() => setMessage({ text: '', variant: '', show: false })}
            className="mb-3"
          >
            {message.text}
          </Alert>
        )}

        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Housekeeping</Modal.Title>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="close-button"
            >
              ×
            </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="clean-status" title="Clean Status">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Clean Statuses</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddCleanStatus}
                      size="sm"
                    >
                      ➕ Add Clean Status
                    </Button>
                  </div>
                  
                  {loadingCleanStatuses ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Description</th>
                            <th>Sort Order</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cleanStatuses.map((cs) => (
                            <tr key={cs.id}>
                              <td><code>{cs.code}</code></td>
                              <td>{cs.description}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-info sort-order-badge">
                                    {cs.sort_order || 0}
                                  </span>
                                  <small className="text-muted">
                                    #{cleanStatuses.indexOf(cs) + 1}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <span className={`badge ${cs.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {cs.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <div className="sort-order-buttons">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => moveCleanStatus(cs.id, 'up')}
                                    disabled={cleanStatuses.indexOf(cs) === 0}
                                    title="Move Up"
                                  >
                                    ⬆️
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => moveCleanStatus(cs.id, 'down')}
                                    disabled={cleanStatuses.indexOf(cs) === cleanStatuses.length - 1}
                                    title="Move Down"
                                  >
                                    ⬇️
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEditCleanStatus(cs)}
                                    title="Edit"
                                  >
                                    ✏️
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => deleteCleanStatus(cs.id)}
                                    title="Delete"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="clean-zones" title="Clean Zones">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Cleaning Zones</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddCleaningZone}
                      size="sm"
                    >
                      ➕ Add Cleaning Zone
                    </Button>
                  </div>
                  
                  {loadingCleaningZones ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Description</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cleaningZones.map((cz) => (
                            <tr key={cz.id}>
                              <td><code>{cz.code}</code></td>
                              <td>{cz.description}</td>
                              <td>
                                <span className={`badge ${cz.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {cz.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditCleaningZone(cz)}
                                  className="me-2"
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deleteCleaningZone(cz.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Clean Status Modal */}
        <Modal 
          show={showCleanStatusModal} 
          onHide={handleCloseCleanStatusModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {cleanStatusModalMode === 'add' ? 'Add Clean Status' : 'Edit Clean Status'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Code *</Form.Label>
                <Form.Control
                  type="text"
                  value={cleanStatusForm.code}
                  onChange={(e) => handleCleanStatusInputChange('code', e.target.value)}
                  isInvalid={!!cleanStatusErrors.code}
                  placeholder="e.g., CLEAN, DIRTY, IN_PROGRESS"
                />
                <Form.Control.Feedback type="invalid">
                  {cleanStatusErrors.code}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cleanStatusForm.description}
                  onChange={(e) => handleCleanStatusInputChange('description', e.target.value)}
                  placeholder="e.g., Unit is clean and ready for guests"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sort Order *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={cleanStatusForm.sort_order}
                  onChange={(e) => handleCleanStatusInputChange('sort_order', parseInt(e.target.value) || 0)}
                  isInvalid={!!cleanStatusErrors.sort_order}
                  placeholder="e.g., 1, 2, 3 (lower numbers appear first)"
                />
                <Form.Control.Feedback type="invalid">
                  {cleanStatusErrors.sort_order}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Lower numbers appear first in lists. Use this to control the order of clean statuses.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={cleanStatusForm.active}
                  onChange={(e) => handleCleanStatusInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCleanStatusModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveCleanStatus}
              disabled={savingCleanStatus}
            >
              {savingCleanStatus ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Clean Zones Modal */}
        <Modal 
          show={showCleaningZoneModal} 
          onHide={handleCloseCleaningZoneModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {cleaningZoneModalMode === 'add' ? 'Add Cleaning Zone' : 'Edit Cleaning Zone'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Code *</Form.Label>
                <Form.Control
                  type="text"
                  value={cleaningZoneForm.code}
                  onChange={(e) => handleCleaningZoneInputChange('code', e.target.value)}
                  isInvalid={!!cleaningZoneErrors.code}
                  placeholder="e.g., ZONE_A, ZONE_B, ZONE_POOL"
                />
                <Form.Control.Feedback type="invalid">
                  {cleaningZoneErrors.code}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cleaningZoneForm.description}
                  onChange={(e) => handleCleaningZoneInputChange('description', e.target.value)}
                  placeholder="e.g., Zone A - Main Building Ground Floor"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={cleaningZoneForm.active}
                  onChange={(e) => handleCleaningZoneInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCleaningZoneModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveCleaningZone}
              disabled={savingCleaningZone}
            >
              {savingCleaningZone ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupHousekeepingScreen;
